"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ShoppingCart, TrendingUp, ChevronLeft } from "lucide-react";

const categories = [
    { id: "all", name: "همه محصولات" },
    { id: "electronics", name: "الکترونیک" },
    { id: "clothing", name: "پوشاک" },
    { id: "home", name: "خانه و آشپزخانه" },
    { id: "beauty", name: "زیبایی و سلامت" },
];

const products = [
    {
        id: 1,
        title: "گوشی موبایل سامسونگ گلکسی S23",
        price: 25000000,
        image: "/gallery/1.jpg",
        category: "electronics",
    },
    {
        id: 2,
        title: "لپ تاپ اپل مک‌بوک پرو",
        price: 45000000,
        image: "/gallery/2.jpg",
        category: "electronics",
    },
    {
        id: 3,
        title: "کفش ورزشی نایک",
        price: 3500000,
        image: "/gallery/3.jpg",
        category: "clothing",
    },
    {
        id: 4,
        title: "ماشین لباسشویی سامسونگ",
        price: 15000000,
        image: "/gallery/4.jpg",
        category: "home",
    },
    {
        id: 5,
        title: "عطر مردانه دیور",
        price: 4500000,
        image: "/gallery/5.jpg",
        category: "beauty",
    },
    {
        id: 6,
        title: "تلویزیون سامسونگ 55 اینچ",
        price: 20000000,
        image: "/gallery/6.jpg",
        category: "electronics",
    },
    {
        id: 7,
        title: "کفش ورزشی نایک",
        price: 3500000,
        image: "/gallery/3.jpg",
        category: "clothing",
    },
];

export const ProductsGrid = () => {
    const [selectedCategory, setSelectedCategory] = useState("all");

    const filteredProducts = selectedCategory === "all" 
        ? products 
        : products.filter(product => product.category === selectedCategory);

    return (
        <div className="relative bg-background/40 backdrop-blur-sm rounded-3xl border border-border/50 px-7 py-5 overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0 overflow-hidden opacity-10">
                <div className="absolute top-0 -right-20 w-[300px] h-[300px] bg-primary rounded-full blur-[80px]" />
                <div className="absolute bottom-0 -left-20 w-[250px] h-[250px] bg-primary rounded-full blur-[60px]" />
            </div>

            <div className="relative z-10 rounded-3xl">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8 ">
                    <div className="flex items-center gap-3">
                        <div className="bg-primary/20 rounded-xl p-2">
                            <TrendingUp className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-foreground">
                                <span className="text-primary">محصولات </span>
                                <span>فروشگاه</span>
                            </h2>
                            <p className="text-muted-foreground text-sm mt-1">محصولات پرفروش و محبوب</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-secondary/20 p-1 rounded-xl">
                            {categories.map((category) => (
                                <button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`md:px-3 px-1 py-1.5 rounded-lg md:text-sm text-xs transition-all duration-300 ${
                                        selectedCategory === category.id
                                            ? "bg-primary text-primary-foreground"
                                            : "text-secondary-foreground hover:bg-secondary/30"
                                    }`}
                                >
                                    {category.name}
                                </button>
                            ))}
                        </div>
                        <Link
                            href="/products"
                            className="group hidden md:flex items-center gap-2 bg-primary/20 hover:bg-primary/30 text-primary px-4 py-2 rounded-xl transition-all duration-300"
                        >
                            <span className="text-sm font-medium">مشاهده همه</span>
                            <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                        </Link>
                    </div>
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 ">
                    {filteredProducts.map((product) => (
                        <div 
                            key={product.id} 
                            className="group relative flex flex-col justify-between overflow-hidden rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm   transition-all duration-300"
                        >
                            <div className="relative aspect-square overflow-hidden">
                                <Image
                                    src={product.image}
                                    alt={product.title}
                                    fill
                                    className="object-cover transition-transform duration-300 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </div>

                            <div className="flex flex-col justify-between text-center items-center space-y-3 p-4">
                                <h3 className="text-sm font-medium text-foreground/90 line-clamp-2 group-hover:text-foreground transition-colors">
                                    {product.title}
                                </h3>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-md font-bold text-primary">
                                            {product.price.toLocaleString()}
                                        </span>
                                        <span className="text-sm text-muted-foreground">تومان</span>
                                    </div>
                                </div>

                                <Button
                                    variant='outline'
                                    className="w-full gap-x-2 hover:bg-primary hover:text-primary-foreground transition-all duration-300 hover:scale-105"
                                    size="sm"
                                >
                                    <ShoppingCart className="h-4 w-4" />
                                    افزودن به سبد خرید
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}; 